/**
 * PushService —— Web Push 订阅管理（OneSignal v16）
 *
 * 职责：
 * 1. 按需懒加载 OneSignal SDK script
 * 2. 用 usersTable.slug 作 externalId 绑定订阅
 * 3. 暴露 optIn / optOut / isSubscribed 给 UI
 * 4. 跟 AuthService 配合：登录后 login，登出后 logout
 *
 * 配置缺失（meta 标签为空）→ 整个服务 noop，不报错
 */

import type {
  IOneSignalOneSignal,
  SubscriptionChangeEvent,
} from "react-onesignal";
import { loadScript } from "@/utils/loader";
import { authService } from "./AuthService";

const SDK_URL = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";

export class PushService {
  private static instance: PushService | null = null;
  private appId = "";
  private ready = false;
  private readyPromise: Promise<IOneSignalOneSignal | null> | null = null;
  private subscriptionListeners = new Set<(optedIn: boolean) => void>();

  static getInstance(): PushService {
    if (!PushService.instance) PushService.instance = new PushService();
    return PushService.instance;
  }

  private constructor() {}

  /**
   * 入口：读 meta 标签拿 appId，注入 SDK，绑定登录态。
   * 多次调用幂等。
   */
  async init(): Promise<void> {
    if (this.readyPromise) {
      console.debug("[PushService] Already initializing or ready, waiting...");
      return void (await this.readyPromise);
    }

    const appId = window.onesignalAppId;
    if (!appId) {
      console.info("[PushService] OneSignal app id missing, skip init");
      return;
    }
    this.appId = appId;
    console.debug("[PushService] Starting init with appId:", appId);

    this.readyPromise = this.loadAndInit();
    const sdk = await this.readyPromise;
    if (!sdk) {
      console.warn("[PushService] SDK failed to load");
      return;
    }

    // 同步登录态：externalId 真源是 AuthService（fromStorage / fromDOM / signin 事件都会先灌进去）
    //
    // 未登录时必须显式 logout，不能 skip——OneSignal SDK 把 externalId 缓存进
    // IndexedDB，跨会话保留。公共电脑场景：用户 A 上次登录 → 缓存 A 的 externalId →
    // 用户 B 今天来用（未登录） → 若不 logout，SDK 还认 A，推送会发到 A 的订阅，
    // B 在自己浏览器收到陌生人的通知。logout() 是幂等的，匿名状态调也安全。
    await this.syncLoginState(sdk, authService.externalId);

    // 订阅事件桥接到 UI 监听器
    sdk.User.PushSubscription.addEventListener(
      "change",
      (e: SubscriptionChangeEvent) => {
        const optedIn = e.current.optedIn;
        console.debug("[PushService] Subscription changed:", optedIn);
        this.subscriptionListeners.forEach((cb) => {
          cb(optedIn);
        });
      },
    );

    // 设置初始语言标签，确保推送按语言过滤时能精准匹配
    await this.syncLanguageTag(sdk);
    // audience / tz 由 syncLoginState 按登录状态统一管，这里不再重复打

    // #4 语言切换：Unpoly 局部刷新换站点语言（URL 含 lang 前缀）后，
    // <html lang> 会变但 PushService 不会重 init，需在导航事件里重对齐 language tag
    up.on("up:location:changed", () => {
      void this.syncLanguageTag(sdk);
    });

    // 跟 AuthService 联动：订阅状态变更（覆盖本地 + 跨标签页 BroadcastChannel）。
    // 用 onStateChange 而非 up.on('auth:signin') 的原因：远端 BroadcastChannel 同步
    // 不走 up 事件系统，A 标签页登录后 B 标签页的 OneSignal SDK 收不到通知，
    // 会继续认旧 externalId 推送给陌生人。
    authService.onStateChange(async (state) => {
      console.debug("[PushService] AuthService state changed:", state);
      await this.syncLoginState(sdk, state.externalId);
    });

    this.ready = true;
    console.debug("[PushService] Init complete, ready=true");

    const state = await this.getSubscriptionState();
    console.info(
      `[PushService] Initial subscription state: ${state.isSubscribed ? "Subscribed" : "Not Subscribed"} (Permission: ${state.permission})`,
    );
    up.emit("push:ready", { state });
  }

  isReady(): boolean {
    return this.ready;
  }

  /** 等待 SDK 初始化完成（无 appId 时立即 resolve）*/
  waitForReady(): Promise<void> {
    if (!this.readyPromise) return Promise.resolve();
    return this.readyPromise.then(() => {});
  }

  /** 当前是否已订阅（在浏览器允许通知 + 用户 optIn 后为 true） */
  async isSubscribed(): Promise<boolean> {
    const sdk = await this.readyPromise;
    if (!sdk) return false;
    return sdk.User.PushSubscription.optedIn ?? false;
  }

  /** 浏览器原生权限状态：default / granted / denied */
  async permissionState(): Promise<NotificationPermission | "unsupported"> {
    if (!("Notification" in window)) return "unsupported";
    const sdk = await this.readyPromise;
    if (!sdk) return Notification.permission;
    return sdk.Notifications.permissionNative;
  }

  /** 请求权限并订阅 */
  async optIn(): Promise<boolean> {
    console.debug("[PushService] optIn requested");
    const sdk = await this.readyPromise;
    if (!sdk) {
      console.warn("[PushService] optIn failed: SDK not ready");
      return false;
    }
    try {
      // OneSignal v16 的 optIn 内部会触发原生权限请求
      await sdk.User.PushSubscription.optIn();
      const result = sdk.User.PushSubscription.optedIn ?? false;
      console.debug("[PushService] optIn result:", result);
      return result;
    } catch (err) {
      console.warn("[PushService] optIn failed", err);
      return false;
    }
  }

  /** 获取当前订阅状态汇总 */
  async getSubscriptionState() {
    const supported = "Notification" in window;
    const isSubscribed = await this.isSubscribed();
    const permission = await this.permissionState();

    return {
      supported,
      isSubscribed,
      permission, // 'default', 'granted', 'denied'
      canPrompt: supported && !isSubscribed && permission !== "denied",
    };
  }

  /** 取消订阅（保留浏览器权限） */
  async optOut(): Promise<void> {
    console.debug("[PushService] optOut requested");
    const sdk = await this.readyPromise;
    if (!sdk) {
      console.warn("[PushService] optOut failed: SDK not ready");
      return;
    }
    try {
      await sdk.User.PushSubscription.optOut();
      console.debug("[PushService] optOut successful");
    } catch (err) {
      console.warn("[PushService] optOut failed", err);
    }
  }

  /** 注册订阅状态变化监听 */
  onSubscriptionChange(cb: (optedIn: boolean) => void): () => void {
    this.subscriptionListeners.add(cb);
    return () => this.subscriptionListeners.delete(cb);
  }

  // ─── 内部 ─────────────────────────────────────────────────────────────

  /**
   * 把 SDK 的 externalId 状态对齐到传入的目标值，并联动刷新所有 user-level tag。
   *
   * - 有 id → login + audience=member + 移除设备绑定 tag（tz 等，避免多设备覆盖）
   * - 无 id → logout + audience=public + 重打 tz（匿名状态下 User=Subscription=设备，
   *   tz 不会跨设备冲突）；同时覆盖 SDK IndexedDB 缓存的旧 externalId
   *
   * 未来若加 identity-bound tag（role/interests/...），需要：
   *   1. 登录分支：addTag(...) 真值
   *   2. 登出分支：removeTag(...) 清掉残留，否则下个用户继承前一个的 role
   */
  private async syncLoginState(
    sdk: IOneSignalOneSignal,
    externalId: string | null,
  ): Promise<void> {
    if (externalId) {
      try {
        await sdk.login(externalId);
        console.debug("[PushService] login successful:", externalId);
        await this.syncAudienceTag(sdk, "member");
        // #5 多设备冲突：tz 是设备属性，登录后 user 跨设备共享 → 移除避免覆盖
        await this.removeDeviceScopedTags(sdk);
      } catch (err) {
        console.warn("[PushService] login failed", err);
      }
    } else {
      try {
        await sdk.logout();
        console.debug("[PushService] logout successful");
        await this.syncAudienceTag(sdk, "public");
        // 匿名状态 user 一台设备一份，tz 安全可标
        await this.syncTimezoneTag(sdk);
      } catch (err) {
        console.warn("[PushService] logout failed", err);
      }
    }
  }

  /**
   * 移除所有设备绑定 tag，避免同一登录用户在多设备间互相覆盖。
   * 当前仅 tz 一个，future device-scoped tag（theme/device 类型等）也在此清理。
   */
  private async removeDeviceScopedTags(
    sdk: IOneSignalOneSignal,
  ): Promise<void> {
    if (!this.lastSyncedTz) return;
    try {
      sdk.User.removeTag("tz");
      this.lastSyncedTz = "";
      console.debug("[PushService] removed device-scoped tz tag");
    } catch (err) {
      console.warn("[PushService] remove tz tag failed", err);
    }
  }

  /** 记录上一次设置的语言 tag，避免重复调用 */
  private lastSyncedLang = "";

  /** 记录上一次设置的受众 tag，避免重复调用 */
  private lastSyncedAudience = "";

  /** 记录上一次设置的时区 tag，避免重复调用 */
  private lastSyncedTz = "";

  /**
   * 将浏览器解析出的 IANA 时区同步到 OneSignal tag=tz。
   * 后台可基于 tz 做 segment，按订阅者本地时间窗口定时推送。
   */
  private async syncTimezoneTag(sdk: IOneSignalOneSignal): Promise<void> {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz || tz === this.lastSyncedTz) return;
    console.debug("[PushService] Syncing tz tag:", tz);
    try {
      sdk.User.addTag("tz", tz);
      this.lastSyncedTz = tz;
      console.debug("[PushService] Sync tz tag successful");
    } catch (err) {
      console.warn("[PushService] sync tz tag failed", err);
    }
  }

  /**
   * 将受众标签同步到 OneSignal：匿名 → "public"，登录后 → "member"。
   * 后台可基于 tag=audience 做 segment 切分，定向推送匿名访客或会员。
   */
  private async syncAudienceTag(
    sdk: IOneSignalOneSignal,
    audience: "public" | "member",
  ): Promise<void> {
    if (audience === this.lastSyncedAudience) {
      console.debug("[PushService] audience tag already synced:", audience);
      return;
    }
    console.debug("[PushService] Syncing audience tag:", audience);
    try {
      sdk.User.addTag("audience", audience);
      this.lastSyncedAudience = audience;
      console.debug("[PushService] Sync audience tag successful");
    } catch (err) {
      console.warn("[PushService] sync audience tag failed", err);
    }
  }

  /**
   * 将当前页面语言（从 <html lang> 读取）同步到 OneSignal 的 language tag。
   * 推送时使用 filters: [{field: "tag", key: "language", ...}] 按语言过滤订阅者。
   */
  private async syncLanguageTag(sdk: IOneSignalOneSignal): Promise<void> {
    const lang = document.documentElement.lang || "zh";
    if (lang === this.lastSyncedLang) {
      console.debug("[PushService] language tag already synced:", lang);
      return;
    }
    console.debug("[PushService] Syncing language tag:", lang);
    try {
      sdk.User.addTag("language", lang);
      this.lastSyncedLang = lang;
      console.debug("[PushService] Sync language tag successful");
    } catch (err) {
      console.warn("[PushService] sync language tag failed", err);
    }
  }

  private async loadAndInit(): Promise<IOneSignalOneSignal | null> {
    try {
      console.debug("[PushService] Loading and initializing SDK...");
      await this.injectScript();
      const sdk = await this.waitForSdk();
      // 在 SDK init 前等待 SW 注册就绪，避免 OneSignal WM 模块发出
      // "No SW registration for postMessage" 警告（SW 尚未激活时无法接收 postMessage）
      await this.waitForSwReady();
      // serviceWorkerParam.scope 必须跟我们 /sw.js 一致，避免 OneSignal 自己注册第二个 SW
      console.debug(
        "[PushService] SDK loaded, calling init with appId:",
        this.appId,
      );
      // serviceWorkerPath 不能带前导斜杠：OneSignal 内部将 scope ("/") + path 拼接，
      // "/sw.js" → "//sw.js" → 被浏览器解析为协议相对 URL https://sw.js → SecurityError
      // 正确写法是相对路径 "sw.js"，OneSignal 会基于 scope 自动拼成 /sw.js
      await sdk.init({
        appId: this.appId,
        serviceWorkerPath: "sw.js",
        serviceWorkerParam: { scope: "/" },
      });
      console.debug("[PushService] SDK.init successful");
      return sdk;
    } catch (err) {
      console.warn("[PushService] OneSignal init failed", err);
      // 清除 promise 允许下次调用 init() 重试
      this.readyPromise = null;
      return null;
    }
  }

  private async injectScript(): Promise<void> {
    await loadScript(SDK_URL, { defer: true });
  }

  /**
   * 等待 Service Worker 完全就绪且控制当前页面（controller 非 null）。
   *
   * 背景：
   * - `navigator.serviceWorker.ready` 只等 SW registration 进入 activated 状态
   * - OneSignal SDK 内部 SessionManager 使用 `navigator.serviceWorker.controller.postMessage()`
   *   发送消息，若 controller 为 null（首次访问 / 强制刷新后 SW 激活但尚未 claim 页面），
   *   就会出现 "[WM] No SW registration for postMessage" 警告
   * - 需额外监听 `controllerchange` 事件，等 SW 完成 clients.claim() 后再 init
   * - 超时 2s 兜底：无 controller 时不永久阻塞（例如私隐模式或 SW 注册失败场景）
   */
  private async waitForSwReady(): Promise<void> {
    if (!("serviceWorker" in navigator)) return;
    try {
      // 等待 SW registration 进入 activated 状态
      await navigator.serviceWorker.ready;

      // 若已有 controller（SW 已控制当前页面），直接返回
      if (navigator.serviceWorker.controller) {
        console.debug("[PushService] SW already active and controlling page");
        return;
      }

      // controller 为 null：SW 刚激活，等待 clients.claim() 触发 controllerchange
      console.debug(
        "[PushService] SW active but no controller yet, waiting for controllerchange...",
      );
      await new Promise<void>((resolve) => {
        // 2s 超时兜底，避免首次访问且 SW 未能 claim 时永久阻塞
        const timer = setTimeout(() => {
          console.debug(
            "[PushService] SW controllerchange timeout, proceeding anyway",
          );
          resolve();
        }, 2000);
        navigator.serviceWorker.addEventListener(
          "controllerchange",
          () => {
            clearTimeout(timer);
            console.debug("[PushService] SW controller now active");
            resolve();
          },
          { once: true },
        );
      });
    } catch (err) {
      // serviceWorker.ready 在某些浏览器（如 Firefox 隐私模式）可能 reject
      console.debug("[PushService] SW ready check skipped:", err);
    }
  }

  private waitForSdk(): Promise<IOneSignalOneSignal> {
    return new Promise((resolve, reject) => {
      if (window.OneSignal) {
        console.debug("[PushService] window.OneSignal already exists");
        resolve(window.OneSignal);
        return;
      }
      console.debug(
        "[PushService] Waiting for OneSignal in window.OneSignalDeferred...",
      );
      const timer = setTimeout(() => {
        console.error("[PushService] OneSignal SDK init timeout");
        reject(new Error("OneSignal SDK init timeout"));
      }, 10_000);
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push((os) => {
        console.debug("[PushService] OneSignal SDK obtained from Deferred");
        clearTimeout(timer);
        resolve(os);
      });
    });
  }
}

export const pushService = PushService.getInstance();
